import {
  PO_LIST_PER_PAGE,
  PO_PRODUCT_LIST_PER_PAGE,
  PO_VENDOR_LIST_PER_PAGE,
  PO_CATEGORY_LIST_PER_PAGE,
  DEFAULT_LIST_SORT,
} from "../../Components/NewPurchaseOrder/constants";

/**
 * Purchase order query keys — structured for filters + future pagination.
 *
 * Why nested keys?
 * - `all` scopes every PO-related cache (invalidation target for mutations).
 * - `list` segment separates listing from count/detail/autosave (future).
 * - `list(filters)` embeds server-side filter state so each search/sort/vendor
 *   combination has its own cache entry (correct refetch + no stale bleed).
 * - `page` / `limit` in filters prepare infinite scroll / offset APIs without
 *   changing hook signatures later.
 *
 * Invalidation examples (see mutations/purchaseOrders):
 * - After create/update/delete: invalidateQueries({ queryKey: purchaseOrderKeys.all })
 * - After filter-only UI change: no invalidation — key change triggers fetch
 */

export const purchaseOrderKeys = {
  all: ["purchase-orders"],

  lists: () => [...purchaseOrderKeys.all, "list"],

  /**
   * @param {object} filters - Serializable filter object from normalizeListFilters()
   */
  list: (filters) => [...purchaseOrderKeys.lists(), filters],

  counts: () => [...purchaseOrderKeys.all, "count"],

  /** KPI summary cards — merchant-scoped */
  count: (merchantId) => [...purchaseOrderKeys.counts(), merchantId],

  listCounts: () => [...purchaseOrderKeys.all, "list-count"],

  /** Quick-filter chip badges — scoped by vendor filter and search text */
  listCount: (merchantId, vendorId = "all", search = "") => [
    ...purchaseOrderKeys.listCounts(),
    String(merchantId || ""),
    vendorId === "all" ? "all" : String(vendorId),
    String(search || "").trim(),
  ],

  vendors: () => [...purchaseOrderKeys.all, "vendors"],
  vendorList: (filters) => [...purchaseOrderKeys.vendors(), filters],
  vendorById: (merchantId, vendorId) => [
    ...purchaseOrderKeys.vendors(),
    "by-id",
    String(merchantId || ""),
    String(vendorId || ""),
  ],

  poNumbers: () => [...purchaseOrderKeys.all, "po-number"],
  poNumber: (merchantId) => [
    ...purchaseOrderKeys.poNumbers(),
    String(merchantId || ""),
  ],
  poNumberChecks: () => [...purchaseOrderKeys.poNumbers(), "check"],
  poNumberCheck: (merchantId, poNumber) => [
    ...purchaseOrderKeys.poNumberChecks(),
    String(merchantId || ""),
    String(poNumber || ""),
  ],

  details: () => [...purchaseOrderKeys.all, "detail"],
  detail: (merchantId, poId) => [
    ...purchaseOrderKeys.details(),
    String(merchantId || ""),
    String(poId || ""),
  ],

  products: () => [...purchaseOrderKeys.all, "products"],
  /** Product picker — keyed by merchant, vendor, filters, search (page is pagination only). */
  productList: (filters) => [...purchaseOrderKeys.products(), filters],

  categories: () => [...purchaseOrderKeys.all, "categories"],
  categoryList: (filters) => [...purchaseOrderKeys.categories(), filters],
};

/**
 * Stable filter object for query keys and payloads.
 * Keep values JSON-serializable (strings/numbers) for predictable cache keys.
 */
export function normalizeVendorListFilters({
  merchantId,
  search = "",
  perPage = PO_VENDOR_LIST_PER_PAGE,
}) {
  return {
    merchantId: String(merchantId || ""),
    search: String(search || "").trim(),
    perPage,
  };
}

export function normalizeCategoryListFilters({
  merchantId,
  perPage = PO_CATEGORY_LIST_PER_PAGE,
}) {
  return {
    merchantId: String(merchantId || ""),
    perPage,
  };
}

export function normalizeListFilters({
  merchantId,
  search = "",
  status = "all",
  sortCol = DEFAULT_LIST_SORT.col,
  sortOrd = DEFAULT_LIST_SORT.ord,
  vendorId = "all",
  perPage = PO_LIST_PER_PAGE,
}) {
  const ord = String(sortOrd || DEFAULT_LIST_SORT.ord).toLowerCase() === "asc"
    ? "asc"
    : "desc";
  return {
    merchantId: String(merchantId || ""),
    search: String(search || "").trim(),
    status: status || "all",
    sortCol: String(sortCol || DEFAULT_LIST_SORT.col),
    sortOrd: ord,
    vendorId: vendorId === "all" ? "all" : String(vendorId),
    perPage,
  };
}

export function normalizeProductListFilters({
  merchantId,
  vendorId = "",
  filterVendorId = "",
  categoryId = "",
  search = "",
  perPage = PO_PRODUCT_LIST_PER_PAGE,
  discAmt,
  excludeDealProducts,
  currentDealType,
  currentDealId,
  forceNodeApi,
  kitScope,
}) {
  const filters = {
    merchantId: String(merchantId || ""),
    vendorId: String(vendorId || ""),
    filterVendorId: String(filterVendorId || ""),
    categoryId: String(categoryId || ""),
    search: String(search || "").trim(),
    perPage,
  };
  // Only present for the item-coupon / BOGO / Mix & Match pickers; omitted
  // otherwise so `disc_amt` never leaks into the PO / stocktake product-list payload.
  if (discAmt !== undefined && discAmt !== null) {
    filters.discAmt = String(discAmt);
  }
  // Deal pickers only — must be part of the query key so create vs edit caches
  // (and different deal ids) do not collide.
  if (excludeDealProducts !== undefined && excludeDealProducts !== null) {
    filters.excludeDealProducts = String(excludeDealProducts);
  }
  if (currentDealType) {
    filters.currentDealType = String(currentDealType);
  }
  if (currentDealId !== undefined && currentDealId !== null && currentDealId !== "") {
    filters.currentDealId = String(currentDealId);
  }
  // Keep Node vs Elastic responses in separate caches.
  if (forceNodeApi) {
    filters.forceNodeApi = true;
  }
  if (kitScope) {
    filters.kitScope = String(kitScope);
  }
  return filters;
}
